// ─── AONE Design System — Token Layer v6 ────────────────────────────────────
// "Angna Atelier" — ivory paper, bronze/gold accents, ember highlights,
// and ink typography. ALL legacy keys (T.rose, T.blush, T.gold, T.terra,
// T.sage, T.module.*, T.card.*, etc.) are preserved with repainted values so
// ERP modules inherit the style-guide colour family without per-page edits.

// ── Global type scale ───────────────────────────────────────────────────────
// ONE number resizes every font in the app. The ladder below is the v6 baseline
// (1.0); TYPE_SCALE multiplies it. Was 0.8 (20% reduction) from 2026-07-25 to
// 2026-07-26 — reverted to 1.0 after complaints the app now read too small.
// To resize the whole UI again, change TYPE_SCALE — never edit the rungs.
export const TYPE_SCALE = 1.0;

const TYPE_BASE = {
  xxs:  10,   // micro labels, badges, timestamps
  xs:   11,   // dense captions, table meta
  sm:   12,   // secondary body
  base: 13,   // body
  md:   14,   // emphasized body
  lg:   15,   // card titles
  xl:   17,   // section headings
  xxl:  19,   // page headings
  xxxl: 22,   // hero
  huge: 28,   // display
  giant: 36,  // jumbo / splash
};

// Round to the nearest 0.5px — keeps rungs distinct without sub-pixel mush.
const scaleType = (px) => Math.round(px * TYPE_SCALE * 2) / 2;

export const T = {
  // ── Surfaces ──────────────────────────────────────────────────────────────
  bg:          '#F8F7F3',                    // Warm White (brand palette) — editorial body
  bgCinema:    '#241914',                    // dark umber (Login page only)
  surface:     '#FFFFFF',                    // card surface — pure white for real contrast against paper
  surfaceAlt:  '#F5EFE6',                    // wells, zebra rows, nested sections
  surfaceSolid:'#FFFFFF',
  sidebar:     '#FFFFFF',                    // sidebar background (legacy alias)
  overlay:     'rgba(38,30,24,0.42)',        // ink-tinted overlay

  // ── Typography ────────────────────────────────────────────────────────────
  text:        '#111111',                    // Ink Black (brand palette)
  textSec:     '#544A40',                    // ink-soft
  textTer:     '#75695C',
  textMuted:   '#7D7468',                    // muted — darkened for legible contrast on paper
  muted:       '#7D7468',  // shorthand alias — same as textMuted
  textInv:     '#FFFFFF',

  // ── Borders ───────────────────────────────────────────────────────────────
  border:      '#E8DDCC',                    // hairline
  borderStrong:'#D7C4AA',
  borderGlass: 'rgba(255,255,255,0.85)',
  borderFocus: '#6E1F2B',                    // Deep Burgundy focus ring
  divider:     '#EFE3D2',
  ruler:       '#F1E6D8',                    // table cell vertical separators

  // ── Role palette — Fusion C2 (SAP × Apple, DENSE pastels) ───────────────
  // Use T.role.<name> for KPI ribbons, status pills, filter chips. Each role
  // wears its color — fills are ~40% saturation, inks deep enough for WCAG AA.
  role: {
    lilac:   { fill: '#F4E6D2', ink: '#A66A2C' },   // bronze — Summary / Total / R&D
    lemon:   { fill: '#F4E9D8', ink: '#8A5A1E' },   // amber — Pending / Queued
    coral:   { fill: '#F4E4DD', ink: '#8A4A2C' },   // clay  — In Progress / Active
    mint:    { fill: '#E7EDE7', ink: '#3F5540' },   // sage  — Completed / Success
    blush:   { fill: '#F5E0E3', ink: '#8A2536' },   // rosewood — Critical / On-Time
    apricot: { fill: '#F3ECDD', ink: '#7D5E28' },   // gold  — Fill Rate / Warning
    sage:    { fill: '#E6ECF0', ink: '#3F5A6E' },   // sky   — Output / Steady
  },

  // ── Primary accent — Deep Burgundy (palette: White / Burgundy / Ink Black) ────
  accent:       '#6E1F2B',                   // deep burgundy — primary action
  accentHover:  '#591722',                   // deeper for hover
  accentDim:    'rgba(110,31,43,0.10)',
  accentMid:    'rgba(110,31,43,0.18)',
  accentSoft:   '#F3E7E6',                   // burgundy tint — soft fills
  accentLight:  '#E3C7CA',                   // burgundy tint — hovers

  // ── Per-module accent palette — Angna mix ────────────────
  // Full editorial reskin: modules use a coordinated mix of bronze, gold, clay, sage, slate, and umber. Keys preserved so pages
  // referencing T.module.<name> (or variant="mint|peach|…") still resolve.
  module: {
    sales:         { accent:'#6E1F2B', hover:'#591722', soft:'#F3E7E6', mid:'rgba(110,31,43,0.18)', hero:'linear-gradient(135deg, #3F0F16 0%, #6E1F2B 55%, #A85563 100%)' },
    procurement:   { accent:'#8B5E3C', hover:'#6F4E37', soft:'#F0E7DC', mid:'rgba(139,94,60,0.18)', hero:'linear-gradient(135deg, #4E3629 0%, #8B5E3C 58%, #C49A6C 100%)' },
    production:    { accent:'#9C3F2F', hover:'#743024', soft:'#F5E2DC', mid:'rgba(156,63,47,0.18)', hero:'linear-gradient(135deg, #5A241C 0%, #9C3F2F 58%, #C77356 100%)' },
    inventory:     { accent:'#56634A', hover:'#3F4A36', soft:'#E8EDE2', mid:'rgba(86,99,74,0.18)', hero:'linear-gradient(135deg, #30382B 0%, #56634A 58%, #8B956F 100%)' },
    finishedGoods: { accent:'#D4A574', hover:'#A66A2C', soft:'#F7EDD8', mid:'rgba(212,165,116,0.22)', hero:'linear-gradient(135deg, #7A4A1C 0%, #D4A574 62%, #F0D4A2 100%)' },
    packaging:     { accent:'#B46A4A', hover:'#8A4A2C', soft:'#F7E8DF', mid:'rgba(180,106,74,0.18)', hero:'linear-gradient(135deg, #70412E 0%, #B46A4A 58%, #D8A07F 100%)' },
    finance:       { accent:'#6F4E37', hover:'#4E3629', soft:'#F0E7DC', mid:'rgba(111,78,55,0.18)', hero:'linear-gradient(135deg, #2B211A 0%, #6F4E37 58%, #A66A2C 100%)' },
    rd:            { accent:'#8B5E3C', hover:'#6F4E37', soft:'#F2E9DE', mid:'rgba(139,94,60,0.18)', hero:'linear-gradient(135deg, #3A2A22 0%, #8B5E3C 58%, #D4A574 100%)' },
    customerCare:  { accent:'#4D6472', hover:'#394C57', soft:'#E6ECF0', mid:'rgba(77,100,114,0.18)', hero:'linear-gradient(135deg, #26343D 0%, #4D6472 58%, #86A0AB 100%)' },
    hr:            { accent:'#A66A2C', hover:'#7A4A1C', soft:'#F4E6D2', mid:'rgba(166,106,44,0.18)', hero:'linear-gradient(135deg, #6F4E37 0%, #A66A2C 55%, #D4A574 100%)' },
    teal:          { accent:'#4D6472', hover:'#394C57', soft:'#E6ECF0', mid:'rgba(77,100,114,0.18)', hero:'linear-gradient(135deg, #26343D 0%, #4D6472 58%, #86A0AB 100%)' },
    slate:         { accent:'#544A40', hover:'#241F1A', soft:'#F1E9DF', mid:'rgba(84,74,64,0.18)', hero:'linear-gradient(135deg, #241F1A 0%, #544A40 60%, #9A8F82 100%)' },
    garnet:        { accent:'#9C3F2F', hover:'#743024', soft:'#F5E2DC', mid:'rgba(156,63,47,0.18)', hero:'linear-gradient(135deg, #5A241C 0%, #9C3F2F 58%, #C77356 100%)' },
  },

  // ── Semantic palette (v3/v4 keys preserved, repainted to editorial family) ─
  terra:       '#A66A2C',                    // bronze (legacy primary alias)
  terraSoft:   'rgba(166,106,44,0.12)',
  sage:        '#56634A',
  sageSoft:    'rgba(86,99,74,0.12)',
  sand:        '#D4A574',
  sandSoft:    'rgba(212,165,116,0.16)',
  lilac:       '#8B5E3C',                    // remapped lilac→bronze
  lilacSoft:   'rgba(166,106,44,0.12)',
  sky:         '#4D6472',                    // editorial slate-blue (info/steady)
  skySoft:     'rgba(63,90,110,0.12)',
  umber:       '#2B211A',                    // dark umber panel base

  // ── Legacy brand palette (kept for page components that reference these) ──
  rose:        '#9C3F2F',                    // remapped rose→ember
  roseSoft:    '#F5E2DC',
  blush:       '#B46A4A',                    // lighter clay
  blushSoft:   '#F7E8DF',
  gold:        '#D4A574',
  goldSoft:    '#F7EDD8',
  oud:         '#6F4E37',                    // warm brown (kept)
  oudSoft:     '#F0E7DC',
  aqua:        '#56634A',                    // remapped aqua→sage
  aquaSoft:    '#E7EDE7',
  lavender:    '#8B5E3C',                    // remapped lavender→bronze
  lavenderSoft:'#F4E6D2',

  // ── Functional States ─────────────────────────────────────────────────────
  success:       '#4C6152',                  // muted sage — matches the editorial palette (was saturated forest green)
  successBg:     '#E7EDE7',
  successBorder: '#4C6152',

  // Same story as danger: the old #D97706 failed AA even on plain WHITE
  // (3.19:1), let alone on its own tint (2.87:1). Amber and violet are the two
  // hues where "readable" and "vivid" diverge most sharply at small sizes.
  warning:       '#92400E',
  warningBg:     '#FFF1E0',
  warningBorder: '#92400E',

  // Text-safe by default. The previous #DC2626 read fine on white (4.83:1) but
  // failed WCAG AA the moment it sat on its own tint — 3.95:1 on dangerBg —
  // and the app puts red text on red fills in ~186 places (status pills,
  // attendance cells, dashboard chips). Rather than a second "strong" red for
  // those, the one danger red is dark enough for all of them: 6.80:1 on
  // dangerBg, 8.31:1 on white, 8.31:1 for white text on a solid danger fill.
  // Every usage improved; none regressed. ToastContext already used this exact
  // value for its error text, so it was the house colour in practice already.
  danger:        '#991B1B',
  dangerBg:      '#FEE2E2',
  dangerBorder:  '#991B1B',

  // Still purple — the no-blue rule holds — but a readable one. #A78BFA was
  // the worst in the palette: 2.36:1 on its own tint, 2.72:1 on white.
  info:          '#6D28D9',
  infoBg:        '#F4ECFB',
  infoBorder:    '#6D28D9',

  neutral:       '#6B7280',
  neutralBg:     '#F9FAFB',
  neutralBorder: '#D1D5DB',

  // ── Font ──────────────────────────────────────────────────────────────────
  // 3 families total: font (sans) · fontDisplay/fontMark (serif) · fontMono. Nothing else should be loaded.
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  fontDisplay: '"Playfair Display", Georgia, serif',
  fontMark: '"Playfair Display", Georgia, serif',
  fontMono: '"Geist Mono", "SF Mono", "Fira Code", ui-monospace, monospace',

  // ── Spacing (8pt grid) ────────────────────────────────────────────────────
  sp: (n) => `${n * 4}px`,
  space: { 1:4, 2:8, 3:12, 4:16, 5:20, 6:24, 8:32, 10:40, 12:48, 16:64 },

  // ── Border radius (rounder — premium Apple feel) ──────────────────────────
  r: { sm:8, md:12, lg:16, xl:20, xxl:24, pill:999 },

  // ── Shadows (Apple-tier — soft, layered depth) ────────────────────────────
  shadow: {
    none:  'none',
    sm:    '0 1px 2px rgba(29,26,22,0.04)',
    md:    '0 1px 3px rgba(29,26,22,0.05)',
    lg:    '0 4px 14px rgba(29,26,22,0.06)',
    xl:    '0 10px 30px rgba(29,26,22,0.08)',
    hover: '0 6px 18px rgba(166,106,44,0.12)',
    panel: '0 -1px 10px rgba(29,26,22,0.04)',
    glass: '0 4px 20px rgba(29,26,22,0.06)',
    green: '0 3px 12px rgba(166,106,44,0.16)',   // legacy key → bronze
  },

  // ── Typography scale ──────────────────────────────────────────────────────
  // The single source of truth for font sizes (plain px numbers, so `fontSize: T.type.sm`
  // is a drop-in for a hardcoded number). Derived from TYPE_BASE × TYPE_SCALE at the top
  // of this file — resize the app by changing TYPE_SCALE, not these rungs. Do NOT hardcode
  // fontSize px anymore; add a rung to TYPE_BASE if you need a size that doesn't exist.
  type: Object.fromEntries(
    Object.entries(TYPE_BASE).map(([rung, px]) => [rung, scaleType(px)])
  ),

  // ── Animation ─────────────────────────────────────────────────────────────
  ease:    'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  spring:  'cubic-bezier(0.34, 1.56, 0.64, 1)',
  duration:{ fast: '120ms', base: '180ms', slow: '300ms' },

  // ── Z-index ───────────────────────────────────────────────────────────────
  z: { dropdown: 1000, sticky: 1100, panel: 1200, modal: 1300, toast: 1400 },

  // ── Card tint palette ─────────────────────────────────────────────────────
  // Variant names preserved so AoneCard variant="rose|blush|gold|oud|aqua|
  // lavender|terra|sage|sand|lilac|sky|mint|peach|coral|apricot" still resolve.
  // Repainted into the editorial (ivory/bronze) family.
  card: {
    // Module-aligned variants (preferred for new code) — all Angna-forward
    terra:    { bg: '#F4E6D2', accent: '#A66A2C', border: 'rgba(166,106,44,0.18)' },
    sage:     { bg: '#E7EDE7', accent: '#4C6152', border: 'rgba(76,97,82,0.18)' },
    sand:     { bg: '#F3ECDD', accent: '#9A7B3F', border: 'rgba(154,123,63,0.18)' },
    lilac:    { bg: '#F4E6D2', accent: '#A66A2C', border: 'rgba(166,106,44,0.18)' },
    sky:      { bg: '#E6ECF0', accent: '#3F5A6E', border: 'rgba(63,90,110,0.18)' },
    // Legacy aliases kept for backward compat
    rose:     { bg: '#F4E6D2', accent: '#A66A2C', border: 'rgba(111,44,61,0.20)' },
    blush:    { bg: '#F5E6E9', accent: '#A84A5E', border: 'rgba(168,74,94,0.22)' },
    gold:     { bg: '#F3ECDD', accent: '#9A7B3F', border: 'rgba(154,123,63,0.20)' },
    oud:      { bg: '#F0E7DC', accent: '#6B4F3A', border: 'rgba(107,79,58,0.16)' },
    aqua:     { bg: '#E7EDE7', accent: '#4C6152', border: 'rgba(76,97,82,0.20)' },
    lavender: { bg: '#F4E6D2', accent: '#A66A2C', border: 'rgba(111,44,61,0.20)' },
    // Named set — resolve to editorial tones
    mint:     { bg: '#E7EDE7', accent: '#4C6152', border: 'rgba(76,97,82,0.18)' },
    peach:    { bg: '#F4E9D8', accent: '#9A6B2F', border: 'rgba(154,107,47,0.18)' },
    coral:    { bg: '#F4E4DD', accent: '#8A4A2C', border: 'rgba(138,74,44,0.18)' },
    apricot:  { bg: '#F3ECDD', accent: '#9A7B3F', border: 'rgba(154,123,63,0.18)' },
  },
};

// ── Status chip config — Fusion C2 role palette ─────────────────────────────
// Status maps to a role-token: mint=success, lemon=pending, coral=in-progress,
// lilac=draft/issued (bronze), blush=critical, sage=steady (sky), apricot=warning.
// AoneStatusChip reads {bg, fg, dot}; border preserved for back-compat.
const _R = {
  mint:    { bg: '#E7EDE7', fg: '#3F5540', border: '#4C6152', dot: '#4C6152' },
  lemon:   { bg: '#F4E9D8', fg: '#7D5518', border: '#9A6B2F', dot: '#9A6B2F' },
  coral:   { bg: '#F4E4DD', fg: '#8A4426', border: '#B05A34', dot: '#B05A34' },
  lilac:   { bg: '#F4E6D2', fg: '#A66A2C', border: '#A66A2C', dot: '#A66A2C' },
  blush:   { bg: '#F5E0E3', fg: '#8A2536', border: '#98342C', dot: '#98342C' },
  apricot: { bg: '#F3ECDD', fg: '#7D5E28', border: '#9A7B3F', dot: '#9A7B3F' },
  sage:    { bg: '#E6ECF0', fg: '#3F5A6E', border: '#3F5A6E', dot: '#3F5A6E' },
  red:     { bg: '#F5E3E0', fg: '#8A2A22', border: '#98342C', dot: '#98342C' },
  grey:    { bg: '#F1EDE5', fg: '#6B6459', border: '#D8CFC0', dot: '#8C857A' },
};

export const STATUS_CONFIG = {
  // generic
  active:    _R.mint,
  inactive:  _R.grey,
  pending:   _R.lemon,
  approved:  _R.mint,
  rejected:  _R.red,
  draft:     _R.lilac,
  converted: _R.sage,
  cancelled: _R.red,
  completed: _R.mint,
  dispatched:_R.lilac,
  delivered: _R.mint,
  overdue:   _R.red,
  reserved:  _R.apricot,
  confirmed: _R.mint,
  ready:     _R.mint,
  shortfall: _R.coral,
  // production
  'in production': _R.coral,
  'qc pending':    _R.lemon,
  'in progress':   _R.coral,
  issued:          _R.lilac,
  released:        _R.mint,
  // formulation
  'r&d':               _R.lilac,
  'under review':      _R.lemon,
  'stability testing': _R.lilac,
  // attendance
  present: _R.mint,
  late:    _R.lemon,
  half_day: _R.coral,
  early_leave: _R.apricot,
  absent:  _R.red,
  wfh:     _R.lilac,
  leave:   _R.grey,
  // priority
  urgent:  _R.red,
  high:    _R.coral,
  medium:  _R.lemon,
  low:     _R.mint,
  // payment
  paid:    _R.mint,
  unpaid:  _R.red,
  partial: _R.lemon,
};

export function resolveStatus(status = '') {
  const key = String(status).toLowerCase().trim();
  return STATUS_CONFIG[key] || { bg: '#F9FAFB', fg: '#6E6E73', border: '#D1D5DB', dot: '#9CA3AF' };
}
